
import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, Eye, MapPin, Calendar, DollarSign, Home, Users, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface ApprovedListing {
  id: string;
  title: string;
  address: string;
  price_egp: number;
  deposit_egp: number;
  unit_type: string;
  furnished: boolean;
  gender_rules: string;
  approval_status: string;
  created_at: string;
  approved_at: string;
  owner_id: string;
  photos: string[];
  description: string;
  amenities: string[];
  verified: boolean;
  distance_km: number;
  available_from: string;
  lease_min_months: number;
  profiles: {
    full_name: string;
    email: string;
    phone: string;
  } | null;
  universities: {
    name_en: string;
    name_ar: string;
    city: string;
  } | null;
}

const ApprovedListings = () => {
  const { t, language } = useLanguage();
  const [approvedListings, setApprovedListings] = useState<ApprovedListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState<ApprovedListing | null>(null);

  useEffect(() => {
    fetchApprovedListings();
  }, []);

  const fetchApprovedListings = async () => {
    try {
      setLoading(true);
      
      // Fetch approved listings with university data
      const { data: listingsData, error: listingsError } = await supabase
        .from('listings')
        .select(`
          *,
          universities (
            name_en,
            name_ar,
            city
          )
        `)
        .eq('approval_status', 'approved')
        .order('approved_at', { ascending: false });

      if (listingsError) {
        console.error('Error fetching approved listings:', listingsError);
        toast.error('Failed to load approved listings');
        return;
      }

      // Get unique owner IDs
      const ownerIds = [...new Set(listingsData?.map(listing => listing.owner_id) || [])];
      
      // Fetch profiles for these owners
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email, phone')
        .in('id', ownerIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      }

      // Create a map of profiles by ID for quick lookup
      const profilesMap = new Map();
      profilesData?.forEach(profile => {
        profilesMap.set(profile.id, profile);
      });

      // Transform the data
      const transformedListings = (listingsData || []).map(listing => ({
        ...listing,
        profiles: profilesMap.get(listing.owner_id) || null,
        universities: listing.universities as { name_en: string; name_ar: string; city: string } | null
      }));

      setApprovedListings(transformedListings);
    } catch (error) {
      console.error('Exception fetching approved listings:', error);
      toast.error('Failed to load approved listings');
    } finally {
      setLoading(false);
    }
  };

  const getUnitTypeLabel = (type: string) => {
    const labels = {
      room: { en: 'Private Room', ar: 'غرفة خاصة' },
      shared: { en: 'Shared Room', ar: 'غرفة مشتركة' },
      entire: { en: 'Entire Unit', ar: 'وحدة كاملة' }
    };
    return labels[type as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || type;
  };

  const getGenderLabel = (gender: string) => {
    const labels = {
      any: { en: 'Any', ar: 'أي جنس' },
      female: { en: 'Female Only', ar: 'إناث فقط' },
      male: { en: 'Male Only', ar: 'ذكور فقط' },
      mixed: { en: 'Mixed', ar: 'مختلط' }
    };
    return labels[gender as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || gender;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          {t('approved_listings', 'الإعلانات المعتمدة', 'Approved Listings')}
          <Badge variant="secondary" className="ml-2">
            {approvedListings.length}
          </Badge>
        </CardTitle>
        <CardDescription>
          {t('manage_approved_listings', 'إدارة الإعلانات المعتمدة', 'Manage approved property listings')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {approvedListings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('no_approved_listings', 'لا توجد إعلانات معتمدة', 'No approved listings found')}</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('property', 'العقار', 'Property')}</TableHead>
                  <TableHead>{t('owner', 'المالك', 'Owner')}</TableHead>
                  <TableHead>{t('location', 'الموقع', 'Location')}</TableHead>
                  <TableHead>{t('price', 'السعر', 'Price')}</TableHead>
                  <TableHead>{t('approved_date', 'تاريخ الاعتماد', 'Approved Date')}</TableHead>
                  <TableHead>{t('actions', 'الإجراءات', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {approvedListings.map((listing) => (
                  <TableRow key={listing.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {listing.photos?.[0] && (
                          <img 
                            src={listing.photos[0]} 
                            alt={listing.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <div className="font-medium">{listing.title}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Badge variant="outline" className="text-xs">
                              {getUnitTypeLabel(listing.unit_type)}
                            </Badge>
                            {listing.verified && (
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                <Shield className="h-3 w-3 mr-1" />
                                {t('verified', 'موثق', 'Verified')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{listing.profiles?.full_name || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">{listing.profiles?.email || 'N/A'}</div>
                        {listing.profiles?.phone && (
                          <div className="text-sm text-muted-foreground">{listing.profiles.phone}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="text-sm">{listing.universities?.city || 'N/A'}</div>
                          <div className="text-xs text-muted-foreground">
                            {listing.distance_km}km from campus
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{listing.price_egp.toLocaleString()} EGP</div>
                          <div className="text-sm text-muted-foreground">
                            +{listing.deposit_egp.toLocaleString()} deposit
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {listing.approved_at ? new Date(listing.approved_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedListing(listing)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {t('view_details', 'عرض التفاصيل', 'View Details')}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>{selectedListing?.title}</DialogTitle>
                            <DialogDescription>
                              {t('listing_details', 'تفاصيل الإعلان', 'Listing Details')}
                            </DialogDescription>
                          </DialogHeader>
                          {selectedListing && (
                            <div className="space-y-6">
                              {/* Images */}
                              {selectedListing.photos && selectedListing.photos.length > 0 && (
                                <div className="grid grid-cols-2 gap-4">
                                  {selectedListing.photos.slice(0, 4).map((photo, index) => (
                                    <div key={index} className="aspect-video bg-muted rounded-lg overflow-hidden">
                                      <img 
                                        src={photo} 
                                        alt={`${selectedListing.title} - Image ${index + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Basic Info */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h4 className="font-semibold mb-2">{t('basic_info', 'المعلومات الأساسية', 'Basic Information')}</h4>
                                  <div className="space-y-2 text-sm">
                                    <div><strong>{t('address', 'العنوان', 'Address')}:</strong> {selectedListing.address}</div>
                                    <div><strong>{t('price', 'السعر', 'Price')}:</strong> {selectedListing.price_egp.toLocaleString()} EGP/month</div>
                                    <div><strong>{t('deposit', 'التأمين', 'Deposit')}:</strong> {selectedListing.deposit_egp.toLocaleString()} EGP</div>
                                    <div><strong>{t('unit_type', 'نوع الوحدة', 'Unit Type')}:</strong> {getUnitTypeLabel(selectedListing.unit_type)}</div>
                                    <div><strong>{t('gender_rules', 'قواعد الجنس', 'Gender Rules')}:</strong> {getGenderLabel(selectedListing.gender_rules)}</div>
                                    <div><strong>{t('furnished', 'مفروش', 'Furnished')}:</strong> {selectedListing.furnished ? t('yes', 'نعم', 'Yes') : t('no', 'لا', 'No')}</div>
                                    <div><strong>{t('min_lease', 'أقل مدة إيجار', 'Min Lease')}:</strong> {selectedListing.lease_min_months} months</div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="font-semibold mb-2">{t('owner_info', 'معلومات المالك', 'Owner Information')}</h4>
                                  <div className="space-y-2 text-sm">
                                    <div><strong>{t('name', 'الاسم', 'Name')}:</strong> {selectedListing.profiles?.full_name || 'N/A'}</div>
                                    <div><strong>{t('email', 'البريد الإلكتروني', 'Email')}:</strong> {selectedListing.profiles?.email || 'N/A'}</div>
                                    {selectedListing.profiles?.phone && (
                                      <div><strong>{t('phone', 'الهاتف', 'Phone')}:</strong> {selectedListing.profiles.phone}</div>
                                    )}
                                    <div><strong>{t('university', 'الجامعة', 'University')}:</strong> {language === 'ar' ? selectedListing.universities?.name_ar : selectedListing.universities?.name_en}</div>
                                    <div><strong>{t('distance', 'المسافة', 'Distance')}:</strong> {selectedListing.distance_km}km from campus</div>
                                  </div>
                                </div>
                              </div>

                              {/* Description */}
                              <div>
                                <h4 className="font-semibold mb-2">{t('description', 'الوصف', 'Description')}</h4>
                                <p className="text-sm text-muted-foreground">{selectedListing.description}</p>
                              </div>

                              {/* Amenities */}
                              {selectedListing.amenities && selectedListing.amenities.length > 0 && (
                                <div>
                                  <h4 className="font-semibold mb-2">{t('amenities', 'المرافق', 'Amenities')}</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {selectedListing.amenities.map((amenity, index) => (
                                      <Badge key={index} variant="outline" className="text-xs">
                                        {amenity}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Dates */}
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <strong>{t('available_from', 'متاح من', 'Available From')}:</strong> {new Date(selectedListing.available_from).toLocaleDateString()}
                                </div>
                                <div>
                                  <strong>{t('approved_on', 'معتمد في', 'Approved On')}:</strong> {selectedListing.approved_at ? new Date(selectedListing.approved_at).toLocaleDateString() : 'N/A'}
                                </div>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApprovedListings;
